import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auditService, Audit, AuditFinding, AuditEvidence } from '@/firebase/auditService';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { getTasksForOrganization, TaskWithDetails } from '@/services/taskService';
import { taskCompletionService, TaskCompletion, uploadEvidenceFile } from '@/firebase/taskCompletionService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { formService, Form } from '@/firebase/formService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import AuditReadinessDashboard from './AuditReadinessDashboard';
import AuditQuickReferencePanel from './AuditQuickReferencePanel';
import AuditHistoryView from './AuditHistoryView';
import { generateAuditPdf } from '@/utils/auditPdfGenerator';
import { 
  ArrowLeft, FileCheck, CheckCircle, XCircle, AlertCircle, Clock, 
  Plus, Paperclip, FileText, Search, ChevronDown, ChevronUp,
  Edit, Trash2, Save, X, Upload, Link as LinkIcon, File, ListChecks, FileEdit, Download, Eye,
  Calendar, Shield, Tag, User
} from 'lucide-react';
import { auditReadinessService, AuditReadiness } from '@/services/auditReadinessService';

const AuditExecutionView: React.FC = () => {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  const [audit, setAudit] = useState<Audit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [complianceTemplates, setComplianceTemplates] = useState<ComplianceTemplate[]>([]);
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [taskCompletions, setTaskCompletions] = useState<TaskCompletion[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  
  // UI State
  const [activeSection, setActiveSection] = useState<'templates' | 'tasks' | 'evidence' | 'findings' | 'history'>('templates');
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  
  // Task attachment
  const [showAttachTaskModal, setShowAttachTaskModal] = useState(false);
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  
  // Evidence attachment
  const [showAttachEvidenceModal, setShowAttachEvidenceModal] = useState(false);
  const [selectedComplianceTemplateForEvidence, setSelectedComplianceTemplateForEvidence] = useState<string | null>(null);
  
  // Findings
  const [showFindingModal, setShowFindingModal] = useState(false);
  const [editingFinding, setEditingFinding] = useState<AuditFinding | null>(null);
  const [selectedComplianceTemplateForFinding, setSelectedComplianceTemplateForFinding] = useState<string | null>(null);
  
  // Notes
  const [auditNotes, setAuditNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  
  // Audit completion
  const [readiness, setReadiness] = useState<AuditReadiness | null>(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    if (auditId) {
      loadAudit();
    }
  }, [auditId]);

  useEffect(() => {
    if (audit) {
      loadComplianceTemplates();
      loadTasks();
      loadTaskCompletions();
      loadChecklists();
      loadForms();
      if (audit.notes) {
        setAuditNotes(audit.notes);
      }
      // Load readiness for active audits
      if (audit.status === 'planned' || audit.status === 'in_progress') {
        loadReadiness();
      }
    }
  }, [audit]);

  const loadAudit = async () => {
    if (!auditId) return;

    setIsLoading(true);
    try {
      const result = await auditService.getAudit(auditId);
      if (result.success && result.data) {
        setAudit(result.data);
      } else {
        showNotification(`Error loading audit: ${result.error}`, 'error');
        navigate('/compliance-management?tab=audits');
      }
    } catch (error: any) {
      console.error('Error loading audit:', error);
      showNotification(`Error: ${error.message}`, 'error');
      navigate('/compliance-management?tab=audits');
    } finally {
      setIsLoading(false);
    }
  };

  const loadComplianceTemplates = async () => {
    if (!audit?.complianceTemplateIds || audit.complianceTemplateIds.length === 0) {
      setComplianceTemplates([]);
      return;
    }

    try {
      const templates: ComplianceTemplate[] = [];
      for (const templateId of audit.complianceTemplateIds) {
        const result = await complianceService.getTemplate(templateId);
        if (result.success && result.data) {
          templates.push(result.data);
        }
      }
      setComplianceTemplates(templates);
      // Expand first template by default
      if (templates.length > 0 && expandedTemplates.size === 0) {
        setExpandedTemplates(new Set([templates[0].id || '']));
      }
    } catch (error) {
      console.error('Error loading compliance templates:', error);
    }
  };

  const loadTasks = async () => {
    if (!audit?.locationId || !audit?.organizationId) return;

    try {
      // Get tasks filtered by organization on server side
      const orgTasks = await getTasksForOrganization(audit.organizationId);
      // Filter tasks for this location
      const relevantTasks = orgTasks.filter(task => 
        task.locationId === audit.locationId &&
        audit.complianceTemplateIds?.includes(task.templateId)
      );
      setTasks(relevantTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const loadTaskCompletions = async () => {
    if (!audit?.locationId || !audit?.organizationId) return;

    try {
      const result = await taskCompletionService.getAllCompletions(
        audit.organizationId,
        [audit.locationId]
      );
      if (result.success && result.data) {
        setTaskCompletions(result.data);
      }
    } catch (error) {
      console.error('Error loading task completions:', error);
    }
  };

  const loadChecklists = async () => {
    if (!currentOrganization?.id) return;

    try {
      const result = await checklistService.getAllChecklists(currentOrganization.id);
      if (result.success && result.data) {
        // Filter checklists for this location if applicable
        const relevantChecklists = result.data.filter(checklist => 
          !checklist.locationIds || 
          checklist.locationIds.length === 0 || 
          checklist.locationIds.includes(audit?.locationId || '')
        );
        setChecklists(relevantChecklists);
      }
    } catch (error) {
      console.error('Error loading checklists:', error);
    }
  };

  const loadForms = async () => {
    if (!currentOrganization?.id) return;

    try {
      const result = await formService.getAllForms(currentOrganization.id);
      if (result.success && result.data) {
        setForms(result.data);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
    }
  };

  const handleAttachTask = async (taskId: string) => {
    if (!audit?.id) return;

    try {
      const currentTaskIds = audit.associatedTaskIds || [];
      if (currentTaskIds.includes(taskId)) {
        showNotification('Task is already attached', 'info');
        return;
      }

      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.updateAudit(audit.id, {
        associatedTaskIds: [...currentTaskIds, taskId]
      }, userInfo);

      if (result.success) {
        showNotification('Task attached successfully', 'success');
        await loadAudit();
        await loadTasks();
        setShowAttachTaskModal(false);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error attaching task:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  const handleDetachTask = async (taskId: string) => {
    if (!audit?.id) return;

    try {
      const currentTaskIds = audit.associatedTaskIds || [];
      const updatedTaskIds = currentTaskIds.filter(id => id !== taskId);

      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.updateAudit(audit.id, {
        associatedTaskIds: updatedTaskIds
      }, userInfo);

      if (result.success) {
        showNotification('Task detached successfully', 'success');
        await loadAudit();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error detaching task:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  const handleSaveNotes = async () => {
    if (!audit?.id) return;

    setIsSavingNotes(true);
    try {
      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.updateAudit(audit.id, {
        notes: auditNotes
      }, userInfo);

      if (result.success) {
        showNotification('Notes saved successfully', 'success');
        await loadAudit();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error saving notes:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const getFindingStatus = (templateId: string): 'compliant' | 'non-compliant' | 'partial' | 'not_applicable' | 'pending' => {
    const finding = audit?.findings?.find(f => f.complianceTemplateId === templateId);
    return finding?.status || 'pending';
  };

  const getStatusBadge = (status: string) => {
    const config = {
      compliant: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Compliant' },
      'non-compliant': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Non-Compliant' },
      partial: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Partial' },
      not_applicable: { color: 'bg-gray-100 text-gray-800', icon: X, label: 'N/A' },
      pending: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Pending' },
    };

    const statusConfig = config[status as keyof typeof config] || config.pending;
    const Icon = statusConfig.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        <Icon className="w-3 h-3" />
        {statusConfig.label}
      </span>
    );
  };

  const getTasksForTemplate = (templateId: string): TaskWithDetails[] => {
    return tasks.filter(task => task.templateId === templateId);
  };

  const getTaskCompletionsForTemplate = (templateId: string): TaskCompletion[] => {
    const template = complianceTemplates.find(t => t.id === templateId);
    if (!template) return [];
    
    return taskCompletions.filter(completion => 
      completion.templateName === template.name
    );
  };

  const getEvidenceForTemplate = (templateId: string): AuditEvidence[] => {
    return audit?.evidence?.filter(e => e.complianceTemplateId === templateId) || [];
  };

  const handleRemoveEvidence = async (evidenceId: string) => {
    if (!audit?.id) return;

    try {
      const updatedEvidence = (audit.evidence || []).filter(e => e.id !== evidenceId);
      
      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.updateAudit(audit.id, {
        evidence: updatedEvidence
      }, userInfo);

      if (result.success) {
        showNotification('Evidence removed successfully', 'success');
        await loadAudit();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error removing evidence:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  const toggleTemplateExpansion = (templateId: string) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
        setSelectedTemplate(null);
      } else {
        newSet.add(templateId);
        setSelectedTemplate(templateId);
      }
      return newSet;
    });
  };

  const loadReadiness = async () => {
    if (!audit?.id) return;
    
    try {
      const result = await auditReadinessService.calculateReadiness(audit);
      setReadiness(result);
    } catch (error: any) {
      console.error('Error loading readiness:', error);
    }
  };

  const validateAuditCompletion = (audit: Audit, readiness: AuditReadiness): {
    canComplete: boolean;
    warnings: string[];
    errors: string[];
  } => {
    const warnings: string[] = [];
    const errors: string[] = [];

    // Required: Readiness >= 100%
    if (readiness.overallScore < 100) {
      errors.push(`Readiness score must be 100% (currently ${readiness.overallScore}%)`);
    }

    // Optional: Check for missing findings
    const templatesWithoutFindings = audit.complianceTemplateIds?.filter(
      templateId => !audit.findings?.some(f => f.complianceTemplateId === templateId)
    ) || [];
    if (templatesWithoutFindings.length > 0) {
      warnings.push(`${templatesWithoutFindings.length} compliance template(s) have no findings documented`);
    }

    // Optional: Check for missing evidence
    if (readiness.evidenceAvailability.score < 100) {
      warnings.push(`Some required evidence types are missing (${readiness.evidenceAvailability.availableEvidenceTypes}/${readiness.evidenceAvailability.requiredEvidenceTypes} available)`);
    }

    // Optional: Check for incomplete tasks
    if (readiness.taskCompletion.score < 100) {
      warnings.push(`${readiness.taskCompletion.totalTasks - readiness.taskCompletion.completedTasks} task(s) still pending`);
    }

    return {
      canComplete: errors.length === 0,
      warnings,
      errors
    };
  };

  const handleCompleteAudit = async () => {
    if (!audit?.id || !readiness) return;

    // Validate
    const validation = validateAuditCompletion(audit, readiness);
    
    if (!validation.canComplete) {
      showNotification(validation.errors.join(', '), 'error');
      return;
    }

    // Show confirmation dialog
    setShowCompletionDialog(true);
  };

  const confirmCompleteAudit = async () => {
    if (!audit?.id || !user) return;

    setIsCompleting(true);
    try {
      const userInfo = {
        id: user.id,
        name: user.name || '',
        email: user.email || ''
      };

      // Update status to completed
      const result = await auditService.updateAuditStatus(
        audit.id,
        'completed',
        userInfo
      );

      if (result.success) {
        showNotification('Audit completed successfully', 'success');
        // Reload audit to get updated status
        await loadAudit();
        // Close dialog
        setShowCompletionDialog(false);
        // Clear readiness since audit is now completed
        setReadiness(null);
      } else {
        showNotification(`Error completing audit: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error completing audit:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleExportAudit = () => {
    if (!audit || !currentOrganization) return;

    try {
      const pdfBlob = generateAuditPdf({
        audit,
        organizationName: currentOrganization.name || 'Organization',
        complianceTemplates: complianceTemplates.map(t => ({ id: t.id || '', name: t.name }))
      });

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Audit_${audit.auditTemplateName}_${audit.locationName}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showNotification('Audit report exported successfully', 'success');
    } catch (error: any) {
      console.error('Error exporting audit:', error);
      showNotification(`Error exporting audit: ${error.message}`, 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading audit...</p>
        </div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Audit not found</p>
          <button
            onClick={() => navigate('/compliance-management?tab=audits')}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Back to Audits
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Full Width at Top */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/compliance-management?tab=audits')}
                className="text-gray-600 hover:text-gray-900 p-1"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{audit.auditTemplateName}</h1>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-sm text-gray-600">{audit.locationName}</span>
                  <span className="text-sm text-gray-400">•</span>
                  <span className="text-sm text-gray-600">
                    {audit.scheduledDate?.toDate ? 
                      audit.scheduledDate.toDate().toLocaleDateString() : 
                      'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportAudit}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2"
                title="Export Audit Report"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </button>
              {/* Complete Audit Button - Show when readiness is 100% and audit is in progress */}
              {audit.status === 'in_progress' && readiness && readiness.overallScore >= 100 && (
                <button
                  onClick={handleCompleteAudit}
                  className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                  title="Complete Audit"
                >
                  <CheckCircle className="w-4 h-4" />
                  Complete Audit
                </button>
              )}
              {getStatusBadge(audit.status)}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow p-4 space-y-2">
              <button
                onClick={() => setActiveSection('templates')}
                className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                  activeSection === 'templates'
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4" />
                  Compliance Templates
                </div>
              </button>
              <button
                onClick={() => setActiveSection('tasks')}
                className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                  activeSection === 'tasks'
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Tasks ({audit.associatedTaskIds?.length || 0})
                </div>
              </button>
              <button
                onClick={() => setActiveSection('evidence')}
                className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                  activeSection === 'evidence'
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Evidence
                </div>
              </button>
              <button
                onClick={() => setActiveSection('findings')}
                className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                  activeSection === 'findings'
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Findings ({audit.findings?.length || 0})
                </div>
              </button>
              <button
                onClick={() => setActiveSection('history')}
                className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                  activeSection === 'history'
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  History
                </div>
              </button>
            </nav>

            {/* Quick Reference Panel */}
            {(audit.status === 'planned' || audit.status === 'in_progress') && (
              <div className="mt-4">
                <AuditQuickReferencePanel
                  currentAudit={audit}
                  selectedComplianceTemplateId={selectedTemplate}
                  onCopyFinding={(finding) => {
                    // Pre-fill finding modal with copied finding
                    setEditingFinding({
                      ...finding,
                      id: '', // Clear ID to create new finding
                      auditId: audit.id || '',
                      createdAt: new Date(),
                      updatedAt: new Date()
                    });
                    setSelectedComplianceTemplateForFinding(finding.complianceTemplateId);
                    setShowFindingModal(true);
                  }}
                />
              </div>
            )}

            {/* Notes Section */}
            <div className="mt-4 bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
              <textarea
                value={auditNotes}
                onChange={(e) => setAuditNotes(e.target.value)}
                disabled={audit.status === 'completed' || audit.status === 'cancelled'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm ${
                  audit.status === 'completed' || audit.status === 'cancelled' 
                    ? 'bg-gray-100 cursor-not-allowed' 
                    : ''
                }`}
                rows={4}
                placeholder="Add notes about this audit..."
              />
              {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                <button
                  onClick={handleSaveNotes}
                  disabled={isSavingNotes}
                  className="mt-2 w-full px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 text-sm"
                >
                  {isSavingNotes ? 'Saving...' : 'Save Notes'}
                </button>
              )}
              {(audit.status === 'completed' || audit.status === 'cancelled') && (
                <p className="mt-2 text-xs text-gray-500 italic">Read-only - Audit is {audit.status}</p>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Readiness Dashboard - Show at top of templates section for active audits */}
            {activeSection === 'templates' && (audit.status === 'planned' || audit.status === 'in_progress') && (
              <div className="mb-4">
                <AuditReadinessDashboard audit={audit} showActions={true} />
              </div>
            )}

            {/* Completed Audit Summary - Show for completed audits */}
            {activeSection === 'templates' && audit.status === 'completed' && (
              <div className="mb-4 bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Audit Summary</h3>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    Completed
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Completion</div>
                    <div className="text-2xl font-semibold text-gray-900 mt-1">
                      {audit.completionPercentage || 0}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Findings</div>
                    <div className="text-2xl font-semibold text-gray-900 mt-1">
                      {audit.findings?.length || 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Completed Date</div>
                    <div className="text-sm font-medium text-gray-900 mt-1">
                      {audit.completedAt?.toDate ? 
                        audit.completedAt.toDate().toLocaleDateString() : 
                        'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Reference Panel - Show for active audits */}
            {activeSection === 'templates' && (audit.status === 'planned' || audit.status === 'in_progress') && (
              <div className="mb-4">
                <AuditQuickReferencePanel
                  currentAudit={audit}
                  selectedComplianceTemplateId={selectedTemplate}
                  onCopyFinding={(finding) => {
                    // Pre-fill finding modal with copied finding
                    setEditingFinding({
                      ...finding,
                      id: '', // Clear ID to create new finding
                      auditId: audit.id || '',
                      createdAt: new Date(),
                      updatedAt: new Date()
                    });
                    setSelectedComplianceTemplateForFinding(finding.complianceTemplateId);
                    setShowFindingModal(true);
                  }}
                />
              </div>
            )}

            {activeSection === 'templates' && (
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Compliance Templates ({complianceTemplates.length})
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {complianceTemplates.map((template) => {
                      const isExpanded = expandedTemplates.has(template.id || '');
                      const templateTasks = getTasksForTemplate(template.id || '');
                      const templateCompletions = getTaskCompletionsForTemplate(template.id || '');
                      const findingStatus = getFindingStatus(template.id || '');

                      return (
                        <div key={template.id} className="border border-gray-200 rounded-lg">
                          <div
                            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleTemplateExpansion(template.id || '')}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                  {getStatusBadge(findingStatus)}
                                </div>
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                  <span>Tasks: {templateTasks.length}</span>
                                  <span>Evidence: {templateCompletions.length}</span>
                                  <span>Category: {template.category}</span>
                                </div>
                              </div>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="border-t border-gray-200 p-4 space-y-4">
                              {/* Template Details */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
                                <p className="text-sm text-gray-600">
                                  {template.pass_fail_criteria || 'No details available'}
                                </p>
                              </div>

                              {/* Tasks */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium text-gray-700">
                                    Tasks ({templateTasks.length})
                                  </h4>
                                  {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTemplate(template.id || '');
                                        setShowAttachTaskModal(true);
                                      }}
                                      className="text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                                    >
                                      <Plus className="w-3 h-3" />
                                      Attach Task
                                    </button>
                                  )}
                                </div>
                                {templateTasks.length > 0 ? (
                                  <div className="space-y-2">
                                    {templateTasks.map((task) => (
                                      <div key={task.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                        <div className="flex-1">
                                          <span className="text-sm text-gray-900">{task.templateName}</span>
                                          <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                            task.status === 'completed' ? 'bg-green-100 text-green-700' :
                                            task.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                            'bg-yellow-100 text-yellow-700'
                                          }`}>
                                            {task.status}
                                          </span>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDetachTask(task.id);
                                          }}
                                          className="text-xs text-red-600 hover:text-red-800"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No tasks attached</p>
                                )}
                              </div>

                              {/* Evidence */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium text-gray-700">
                                    Evidence ({getEvidenceForTemplate(template.id || '').length})
                                  </h4>
                                  {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedComplianceTemplateForEvidence(template.id || '');
                                        setShowAttachEvidenceModal(true);
                                      }}
                                      className="text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                                    >
                                      <Plus className="w-3 h-3" />
                                      Attach Evidence
                                    </button>
                                  )}
                                </div>
                                {getEvidenceForTemplate(template.id || '').length > 0 ? (
                                  <div className="space-y-2">
                                    {getEvidenceForTemplate(template.id || '').map((evidence) => (
                                      <div key={evidence.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                        <div className="flex items-center gap-2 flex-1">
                                          {evidence.evidenceType === 'task_completion' && <CheckCircle className="w-4 h-4 text-blue-600" />}
                                          {evidence.evidenceType === 'checklist' && <ListChecks className="w-4 h-4 text-green-600" />}
                                          {evidence.evidenceType === 'form' && <FileEdit className="w-4 h-4 text-purple-600" />}
                                          {evidence.evidenceType === 'direct_upload' && <File className="w-4 h-4 text-gray-600" />}
                                          <div className="flex-1">
                                            <div className="text-sm text-gray-900">
                                              {evidence.sourceName || evidence.fileName || 'Evidence'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {evidence.evidenceType === 'task_completion' && 'Task Completion'}
                                              {evidence.evidenceType === 'checklist' && 'Checklist'}
                                              {evidence.evidenceType === 'form' && 'Form'}
                                              {evidence.evidenceType === 'direct_upload' && 'Direct Upload'}
                                            </div>
                                          </div>
                                        </div>
                                        {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveEvidence(evidence.id);
                                            }}
                                            className="text-xs text-red-600 hover:text-red-800 ml-2"
                                          >
                                            Remove
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No evidence attached</p>
                                )}
                              </div>

                              {/* Finding */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium text-gray-700">Finding</h4>
                                  {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const existingFinding = audit.findings?.find(f => f.complianceTemplateId === template.id);
                                        if (existingFinding) {
                                          setEditingFinding(existingFinding);
                                        }
                                        setSelectedComplianceTemplateForFinding(template.id || '');
                                        setShowFindingModal(true);
                                      }}
                                      className="text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                                    >
                                      {audit.findings?.find(f => f.complianceTemplateId === template.id) ? (
                                        <>
                                          <Edit className="w-3 h-3" />
                                          Edit Finding
                                        </>
                                      ) : (
                                        <>
                                          <Plus className="w-3 h-3" />
                                          Add Finding
                                        </>
                                      )}
                                    </button>
                                  )}
                                  {audit.status === 'completed' && audit.findings?.find(f => f.complianceTemplateId === template.id) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const existingFinding = audit.findings?.find(f => f.complianceTemplateId === template.id);
                                        if (existingFinding) {
                                          setEditingFinding(existingFinding);
                                        }
                                        setSelectedComplianceTemplateForFinding(template.id || '');
                                        setShowFindingModal(true);
                                      }}
                                      className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1"
                                      title="View Finding (Read-only)"
                                    >
                                      <Eye className="w-3 h-3" />
                                      View Finding
                                    </button>
                                  )}
                                </div>
                                {audit.findings?.find(f => f.complianceTemplateId === template.id) ? (
                                  <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-sm text-gray-700">
                                      {audit.findings.find(f => f.complianceTemplateId === template.id)?.notes || 'No notes'}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No finding documented</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'tasks' && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Attached Tasks ({audit.associatedTaskIds?.length || 0})
                  </h2>
                  {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                    <button
                      onClick={() => setShowAttachTaskModal(true)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Attach Task
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {tasks.filter(task => audit.associatedTaskIds?.includes(task.id)).map((task) => (
                    <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{task.templateName}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                            <span>Status: {task.status}</span>
                            {task.dueDate && (
                              <span>Due: {task.dueDate.toDate ? task.dueDate.toDate().toLocaleDateString() : 'N/A'}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDetachTask(task.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Detach
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!audit.associatedTaskIds || audit.associatedTaskIds.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No tasks attached yet</p>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'evidence' && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Evidence ({audit.evidence?.length || 0})
                  </h2>
                  {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                    <button
                      onClick={() => {
                        setSelectedComplianceTemplateForEvidence('');
                        setShowAttachEvidenceModal(true);
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Attach Evidence
                    </button>
                  )}
                </div>

                {audit.evidence && audit.evidence.length > 0 ? (
                  <div className="space-y-4">
                    {complianceTemplates.map((template) => {
                      const templateEvidence = getEvidenceForTemplate(template.id || '');
                      if (templateEvidence.length === 0) return null;

                      return (
                        <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                          <h3 className="font-medium text-gray-900 mb-3">{template.name}</h3>
                          <div className="space-y-2">
                            {templateEvidence.map((evidence) => (
                              <div key={evidence.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                                <div className="flex items-center gap-3 flex-1">
                                  {evidence.evidenceType === 'task_completion' && <CheckCircle className="w-5 h-5 text-blue-600" />}
                                  {evidence.evidenceType === 'checklist' && <ListChecks className="w-5 h-5 text-green-600" />}
                                  {evidence.evidenceType === 'form' && <FileEdit className="w-5 h-5 text-purple-600" />}
                                  {evidence.evidenceType === 'direct_upload' && <File className="w-5 h-5 text-gray-600" />}
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {evidence.sourceName || evidence.fileName || 'Evidence'}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">
                                      {evidence.evidenceType === 'task_completion' && 'Task Completion'}
                                      {evidence.evidenceType === 'checklist' && 'Checklist'}
                                      {evidence.evidenceType === 'form' && 'Form'}
                                      {evidence.evidenceType === 'direct_upload' && 'Direct Upload'}
                                      {evidence.uploadedAt && (
                                        <span> • {evidence.uploadedAt instanceof Date 
                                          ? evidence.uploadedAt.toLocaleDateString()
                                          : new Date(evidence.uploadedAt).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                    {evidence.url && evidence.evidenceType === 'direct_upload' && (
                                      <a
                                        href={evidence.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-primary-600 hover:text-primary-800 mt-1 inline-block"
                                      >
                                        View File →
                                      </a>
                                    )}
                                  </div>
                                </div>
                                {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                                  <button
                                    onClick={() => handleRemoveEvidence(evidence.id)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* General Evidence (not tied to specific compliance template) */}
                    {audit.evidence && audit.evidence.filter(e => !e.complianceTemplateId || e.complianceTemplateId === '').length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-3">General Evidence</h3>
                        <div className="space-y-2">
                          {audit.evidence
                            .filter(e => !e.complianceTemplateId || e.complianceTemplateId === '')
                            .map((evidence) => (
                              <div key={evidence.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                                <div className="flex items-center gap-3 flex-1">
                                  {evidence.evidenceType === 'task_completion' && <CheckCircle className="w-5 h-5 text-blue-600" />}
                                  {evidence.evidenceType === 'checklist' && <ListChecks className="w-5 h-5 text-green-600" />}
                                  {evidence.evidenceType === 'form' && <FileEdit className="w-5 h-5 text-purple-600" />}
                                  {evidence.evidenceType === 'direct_upload' && <File className="w-5 h-5 text-gray-600" />}
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {evidence.sourceName || evidence.fileName || 'Evidence'}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">
                                      {evidence.evidenceType === 'task_completion' && 'Task Completion'}
                                      {evidence.evidenceType === 'checklist' && 'Checklist'}
                                      {evidence.evidenceType === 'form' && 'Form'}
                                      {evidence.evidenceType === 'direct_upload' && 'Direct Upload'}
                                      {evidence.uploadedAt && (
                                        <span> • {evidence.uploadedAt instanceof Date 
                                          ? evidence.uploadedAt.toLocaleDateString()
                                          : new Date(evidence.uploadedAt).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                    {evidence.url && evidence.evidenceType === 'direct_upload' && (
                                      <a
                                        href={evidence.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-primary-600 hover:text-primary-800 mt-1 inline-block"
                                      >
                                        View File →
                                      </a>
                                    )}
                                  </div>
                                </div>
                                {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                                  <button
                                    onClick={() => handleRemoveEvidence(evidence.id)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No evidence attached yet</p>
                    {audit.status !== 'completed' && audit.status !== 'cancelled' && (
                      <button
                        onClick={() => {
                          setSelectedComplianceTemplateForEvidence('');
                          setShowAttachEvidenceModal(true);
                        }}
                        className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                      >
                        Attach Evidence
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'findings' && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Findings ({audit.findings?.length || 0})
                  </h2>
                </div>

                <div className="space-y-3">
                  {audit.findings?.map((finding) => {
                    const template = complianceTemplates.find(t => t.id === finding.complianceTemplateId);
                    return (
                      <div key={finding.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h3 className="font-medium text-gray-900">{template?.name || 'Unknown Template'}</h3>
                            {getStatusBadge(finding.status)}
                          </div>
                          <button
                            onClick={() => {
                              setEditingFinding(finding);
                              setSelectedComplianceTemplateForFinding(finding.complianceTemplateId);
                              setShowFindingModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Edit
                          </button>
                        </div>
                        <p className="text-sm text-gray-700 mt-2">{finding.notes || 'No notes'}</p>
                      </div>
                    );
                  })}
                  {(!audit.findings || audit.findings.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No findings documented yet</p>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'history' && audit && (
              <AuditHistoryView
                currentAudit={audit}
                onCopyFinding={(finding) => {
                  setEditingFinding({
                    ...finding,
                    id: '',
                    auditId: audit.id || '',
                    createdAt: new Date(),
                    updatedAt: new Date()
                  });
                  setSelectedComplianceTemplateForFinding(finding.complianceTemplateId);
                  setShowFindingModal(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Attach Task Modal */}
      {showAttachTaskModal && (
        <AttachTaskModal
          audit={audit}
          selectedTemplate={selectedTemplate}
          existingTaskIds={audit.associatedTaskIds || []}
          onAttach={handleAttachTask}
          onClose={() => {
            setShowAttachTaskModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      {/* Attach Evidence Modal */}
      {showAttachEvidenceModal && (
        <AttachEvidenceModal
          audit={audit}
          complianceTemplateId={selectedComplianceTemplateForEvidence || ''}
          taskCompletions={taskCompletions}
          checklists={checklists}
          forms={forms}
          onClose={() => {
            setShowAttachEvidenceModal(false);
            setSelectedComplianceTemplateForEvidence(null);
          }}
          onSuccess={() => {
            loadTaskCompletions();
            loadAudit();
          }}
        />
      )}

      {/* Finding Modal */}
      {showFindingModal && selectedComplianceTemplateForFinding && (
        <FindingModal
          audit={audit}
          complianceTemplateId={selectedComplianceTemplateForFinding}
          complianceTemplate={complianceTemplates.find(t => t.id === selectedComplianceTemplateForFinding) || null}
          existingFinding={editingFinding}
          onSave={async () => {
            await loadAudit();
            setShowFindingModal(false);
            setEditingFinding(null);
            setSelectedComplianceTemplateForFinding(null);
          }}
          onClose={() => {
            setShowFindingModal(false);
            setEditingFinding(null);
            setSelectedComplianceTemplateForFinding(null);
          }}
        />
      )}
    </div>
  );
};

// Attach Task Modal Component
interface AttachTaskModalProps {
  audit: Audit;
  selectedTemplate?: string | null;
  existingTaskIds: string[];
  onAttach: (taskId: string) => void;
  onClose: () => void;
}

const AttachTaskModal: React.FC<AttachTaskModalProps> = ({
  audit,
  selectedTemplate,
  existingTaskIds,
  onAttach,
  onClose
}) => {
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setIsLoading(true);
    try {
      // Get tasks filtered by organization on server side
      const orgTasks = await getTasksForOrganization(audit.organizationId);
      // Filter tasks for this location
      // Show ALL tasks for this location (not just ones matching templates)
      // This allows attaching tasks that don't match compliance template IDs
      const relevantTasks = orgTasks.filter(task => 
        task.locationId === audit.locationId &&
        !existingTaskIds.includes(task.id)
        // Removed template filter - allow attaching any task from this location
        // This is useful for tasks that don't match compliance template IDs
      );
      setTasks(relevantTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task =>
    !searchTerm || task.templateName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Attach Task</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No tasks available to attach</p>
              <p className="text-xs mt-2">All tasks for this location are already attached, or no tasks exist yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Show info about template matching */}
              {audit.complianceTemplateIds && audit.complianceTemplateIds.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Tasks matching audit compliance templates are automatically included in readiness calculations. 
                    You can also attach other relevant tasks from this location.
                  </p>
                </div>
              )}
              
              {filteredTasks.map((task) => {
                // Check if this task matches one of the audit's compliance template IDs
                const matchesAuditTemplate = audit.complianceTemplateIds?.includes(task.templateId) || false;
                
                return (
                  <div
                    key={task.id}
                    className={`border rounded-lg p-4 hover:shadow-md cursor-pointer transition-all ${
                      matchesAuditTemplate 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => onAttach(task.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-medium text-gray-900 truncate">{task.templateName}</h3>
                          {matchesAuditTemplate && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium flex-shrink-0">
                              Matches Template
                            </span>
                          )}
                          {(task.messageContext || task.checklistContext || !task.templateId) && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium flex-shrink-0">
                              Custom
                            </span>
                          )}
                        </div>
                        
                        {/* Status and Dates Row */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${
                            task.status === 'completed' ? 'bg-green-100 text-green-700' :
                            task.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {task.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                            {task.status === 'overdue' && <AlertCircle className="w-3 h-3" />}
                            {task.status !== 'completed' && task.status !== 'overdue' && <Clock className="w-3 h-3" />}
                            {task.status}
                          </span>
                          
                          {/* Completed Date */}
                          {task.status === 'completed' && task.completedAt && (
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-600" />
                              <span className="font-medium">Completed:</span>
                              {task.completedAt.toDate ? task.completedAt.toDate().toLocaleDateString() : 
                               new Date(task.completedAt).toLocaleDateString()}
                            </span>
                          )}
                          
                          {/* Due Date (for non-completed tasks) */}
                          {task.status !== 'completed' && task.dueDate && (
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">Due:</span>
                              {task.dueDate.toDate ? task.dueDate.toDate().toLocaleDateString() : 
                               new Date(task.dueDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        {/* Metadata Row */}
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                          {/* Risk Level */}
                          {task.riskLevel && (
                            <span className="flex items-center gap-1">
                              <Shield className={`w-3 h-3 ${
                                task.riskLevel.toLowerCase() === 'high' ? 'text-red-600' :
                                task.riskLevel.toLowerCase() === 'medium' ? 'text-yellow-600' :
                                'text-green-600'
                              }`} />
                              <span className={`px-2 py-0.5 rounded font-medium ${
                                task.riskLevel.toLowerCase() === 'high' ? 'bg-red-100 text-red-700' :
                                task.riskLevel.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {task.riskLevel} Risk
                              </span>
                            </span>
                          )}
                          
                          {/* Category */}
                          {task.category && (
                            <span className="flex items-center gap-1">
                              <Tag className="w-3 h-3 text-gray-400" />
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                                {task.category}
                              </span>
                            </span>
                          )}
                          
                          {/* Owner Role */}
                          {task.ownerRole && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">Owner:</span>
                              <span>{task.ownerRole}</span>
                            </span>
                          )}
                          
                          {/* Frequency */}
                          {task.frequency && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">Frequency:</span>
                              <span>{task.frequency}</span>
                            </span>
                          )}
                        </div>
                        
                        {/* Source Context */}
                        {(task.messageContext || task.checklistContext) && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <span className="text-xs text-gray-500">
                              <span className="font-medium">Origin:</span>{' '}
                              {task.messageContext && (
                                <span className="text-purple-600">Created from conversation/message</span>
                              )}
                              {task.checklistContext && (
                                <span className="text-blue-600" title="This task was created from a checklist item during an inspection or checklist run">
                                  Created from checklist item
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        
                        {/* Not in audit templates indicator */}
                        {!matchesAuditTemplate && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <span className="text-xs text-gray-500 italic">
                              This task will be manually tracked (not in audit compliance templates)
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <Plus className="w-5 h-5 text-primary-600" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Attach Evidence Modal Component
interface AttachEvidenceModalProps {
  audit: Audit;
  complianceTemplateId: string;
  taskCompletions: TaskCompletion[];
  checklists: Checklist[];
  forms: Form[];
  onClose: () => void;
  onSuccess: () => void;
}

const AttachEvidenceModal: React.FC<AttachEvidenceModalProps> = ({
  audit,
  complianceTemplateId,
  taskCompletions,
  checklists,
  forms,
  onClose,
  onSuccess
}) => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  
  const [activeTab, setActiveTab] = useState<'tasks' | 'checklists' | 'forms' | 'upload'>('tasks');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTaskCompletions, setSelectedTaskCompletions] = useState<Set<string>>(new Set());
  const [selectedChecklists, setSelectedChecklists] = useState<Set<string>>(new Set());
  const [selectedForms, setSelectedForms] = useState<Set<string>>(new Set());
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Get already attached evidence for this compliance template (or all if empty string)
  const existingEvidence = complianceTemplateId
    ? audit.evidence?.filter(e => e.complianceTemplateId === complianceTemplateId) || []
    : audit.evidence || [];
  const existingTaskCompletionIds = new Set(
    existingEvidence
      .filter(e => e.evidenceType === 'task_completion' && e.sourceId)
      .map(e => e.sourceId!)
  );
  const existingChecklistIds = new Set(
    existingEvidence
      .filter(e => e.evidenceType === 'checklist' && e.sourceId)
      .map(e => e.sourceId!)
  );
  const existingFormIds = new Set(
    existingEvidence
      .filter(e => e.evidenceType === 'form' && e.sourceId)
      .map(e => e.sourceId!)
  );

  // Filter task completions for this location and compliance template
  const relevantTaskCompletions = taskCompletions.filter(completion => 
    completion.locationId === audit.locationId &&
    !existingTaskCompletionIds.has(completion.id || '')
  );

  // Filter checklists for this location
  const relevantChecklists = checklists.filter(checklist =>
    (!checklist.locationIds || checklist.locationIds.length === 0 || checklist.locationIds.includes(audit.locationId || '')) &&
    !existingChecklistIds.has(checklist.id || '')
  );

  // Filter forms
  const relevantForms = forms.filter(form =>
    !existingFormIds.has(form.id || '')
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadingFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!audit.id || !currentOrganization?.id) return;

    // If no compliance template selected, require user to select one or use empty string
    // For now, we'll allow empty string for general evidence
    const templateId = complianceTemplateId || '';

    setIsSaving(true);
    try {
      const newEvidence: AuditEvidence[] = [];
      
      // Add selected task completions
      for (const completionId of selectedTaskCompletions) {
        const completion = taskCompletions.find(c => c.id === completionId);
        if (completion && completion.evidenceUrls && completion.evidenceUrls.length > 0) {
          // Create evidence entry for each file in the completion
          completion.evidenceUrls.forEach((url, idx) => {
            newEvidence.push({
              id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${idx}`,
              auditId: audit.id!,
              complianceTemplateId: templateId,
              evidenceType: 'task_completion',
              sourceId: completionId,
              sourceName: completion.templateName,
              url: url,
              fileName: completion.evidenceFileNames?.[idx] || `Evidence ${idx + 1}`,
              uploadedAt: completion.completionDate,
              uploadedBy: completion.completedBy,
              createdAt: new Date()
            });
          });
        }
      }

      // Add selected checklists
      for (const checklistId of selectedChecklists) {
        const checklist = checklists.find(c => c.id === checklistId);
        if (checklist) {
          newEvidence.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            auditId: audit.id!,
            complianceTemplateId: templateId,
            evidenceType: 'checklist',
            sourceId: checklistId,
            sourceName: checklist.name,
            url: `/checklists/${checklistId}`, // Reference URL
            createdAt: new Date()
          });
        }
      }

      // Add selected forms
      for (const formId of selectedForms) {
        const form = forms.find(f => f.id === formId);
        if (form) {
          newEvidence.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            auditId: audit.id!,
            complianceTemplateId: templateId,
            evidenceType: 'form',
            sourceId: formId,
            sourceName: form.name,
            url: `/forms/${formId}`, // Reference URL
            createdAt: new Date()
          });
        }
      }

      // Upload files and add as direct upload evidence
      for (let i = 0; i < uploadingFiles.length; i++) {
        const file = uploadingFiles[i];
        try {
          setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
          
          const uploadResult = await uploadEvidenceFile(file, {
            organizationId: currentOrganization.id,
            locationId: audit.locationId,
            templateId: templateId || undefined,
            uploadedBy: user ? {
              id: user.id,
              name: user.name || '',
              email: user.email || ''
            } : undefined,
            docType: 'audit_evidence',
            evidenceLabel: file.name
          });

          newEvidence.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            auditId: audit.id!,
            complianceTemplateId: templateId,
            evidenceType: 'direct_upload',
            url: uploadResult.url,
            fileName: file.name,
            uploadedBy: user?.id,
            uploadedAt: new Date(),
            createdAt: new Date()
          });

          setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        } catch (error: any) {
          console.error(`Error uploading file ${file.name}:`, error);
          showNotification(`Error uploading ${file.name}: ${error.message}`, 'error');
        }
      }

      // Update audit with new evidence
      const existingEvidenceList = audit.evidence || [];
      const updatedEvidence = [...existingEvidenceList, ...newEvidence];

      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.updateAudit(audit.id, {
        evidence: updatedEvidence
      }, userInfo);

      if (result.success) {
        showNotification(`Successfully attached ${newEvidence.length} evidence item(s)`, 'success');
        onSuccess();
        onClose();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error attaching evidence:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
      setUploadProgress({});
    }
  };

  const filteredTaskCompletions = relevantTaskCompletions.filter(completion =>
    !searchTerm || completion.templateName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredChecklists = relevantChecklists.filter(checklist =>
    !searchTerm || checklist.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredForms = relevantForms.filter(form =>
    !searchTerm || form.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Attach Evidence</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tasks'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Task Completions ({filteredTaskCompletions.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('checklists')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'checklists'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <ListChecks className="w-4 h-4" />
                Checklists ({filteredChecklists.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('forms')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'forms'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileEdit className="w-4 h-4" />
                Forms ({filteredForms.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'upload'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload Files
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search */}
          {(activeTab === 'tasks' || activeTab === 'checklists' || activeTab === 'forms') && (
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          )}

          {/* Task Completions Tab */}
          {activeTab === 'tasks' && (
            <div className="space-y-2">
              {filteredTaskCompletions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'No matching task completions found' : 'No task completions available'}
                </div>
              ) : (
                filteredTaskCompletions.map((completion) => {
                  const isSelected = selectedTaskCompletions.has(completion.id || '');
                  const hasEvidence = completion.evidenceUrls && completion.evidenceUrls.length > 0;
                  
                  return (
                    <div
                      key={completion.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        isSelected ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                      } ${!hasEvidence ? 'opacity-50' : ''}`}
                      onClick={() => {
                        if (hasEvidence) {
                          setSelectedTaskCompletions(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(completion.id || '')) {
                              newSet.delete(completion.id || '');
                            } else {
                              newSet.add(completion.id || '');
                            }
                            return newSet;
                          });
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{completion.templateName}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {completion.evidenceUrls?.length || 0} file(s) • 
                            {completion.completionDate && (
                              <span> Completed: {completion.completionDate instanceof Date 
                                ? completion.completionDate.toLocaleDateString()
                                : new Date(completion.completionDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {!hasEvidence && (
                            <div className="text-xs text-orange-600 mt-1">No evidence files attached</div>
                          )}
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-primary-600" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Checklists Tab */}
          {activeTab === 'checklists' && (
            <div className="space-y-2">
              {filteredChecklists.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'No matching checklists found' : 'No checklists available'}
                </div>
              ) : (
                filteredChecklists.map((checklist) => {
                  const isSelected = selectedChecklists.has(checklist.id || '');
                  
                  return (
                    <div
                      key={checklist.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        isSelected ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedChecklists(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(checklist.id || '')) {
                            newSet.delete(checklist.id || '');
                          } else {
                            newSet.add(checklist.id || '');
                          }
                          return newSet;
                        });
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{checklist.name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {checklist.items?.length || 0} items
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-primary-600" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Forms Tab */}
          {activeTab === 'forms' && (
            <div className="space-y-2">
              {filteredForms.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'No matching forms found' : 'No forms available'}
                </div>
              ) : (
                filteredForms.map((form) => {
                  const isSelected = selectedForms.has(form.id || '');
                  
                  return (
                    <div
                      key={form.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        isSelected ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedForms(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(form.id || '')) {
                            newSet.delete(form.id || '');
                          } else {
                            newSet.add(form.id || '');
                          }
                          return newSet;
                        });
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{form.name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {form.fields?.length || 0} fields
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-primary-600" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <span className="text-primary-600 hover:text-primary-700 font-medium">
                    Click to select files
                  </span>
                  <span className="text-gray-600"> or drag and drop</span>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  PDF, Images, Documents (Max 10MB per file)
                </p>
              </div>

              {uploadingFiles.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-700">Selected Files:</h3>
                  {uploadingFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between border border-gray-200 rounded p-2">
                      <div className="flex items-center gap-2 flex-1">
                        <File className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      {uploadProgress[file.name] !== undefined && uploadProgress[file.name] < 100 && (
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${uploadProgress[file.name]}%` }}
                          ></div>
                        </div>
                      )}
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Existing Evidence */}
          {existingEvidence.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="font-medium text-gray-700 mb-3">Already Attached ({existingEvidence.length})</h3>
              <div className="space-y-2">
                {existingEvidence.map((evidence) => (
                  <div key={evidence.id} className="flex items-center justify-between border border-gray-200 rounded p-2 bg-gray-50">
                    <div className="flex items-center gap-2 flex-1">
                      {evidence.evidenceType === 'task_completion' && <CheckCircle className="w-4 h-4 text-blue-600" />}
                      {evidence.evidenceType === 'checklist' && <ListChecks className="w-4 h-4 text-green-600" />}
                      {evidence.evidenceType === 'form' && <FileEdit className="w-4 h-4 text-purple-600" />}
                      {evidence.evidenceType === 'direct_upload' && <File className="w-4 h-4 text-gray-600" />}
                      <span className="text-sm text-gray-900">{evidence.sourceName || evidence.fileName || 'Evidence'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || (
              selectedTaskCompletions.size === 0 &&
              selectedChecklists.size === 0 &&
              selectedForms.size === 0 &&
              uploadingFiles.length === 0
            )}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Attaching...' : `Attach Evidence (${selectedTaskCompletions.size + selectedChecklists.size + selectedForms.size + uploadingFiles.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Finding Modal Component
interface FindingModalProps {
  audit: Audit;
  complianceTemplateId: string;
  complianceTemplate: ComplianceTemplate | null;
  existingFinding: AuditFinding | null;
  onSave: () => void;
  onClose: () => void;
}

const FindingModal: React.FC<FindingModalProps> = ({
  audit,
  complianceTemplateId,
  complianceTemplate,
  existingFinding,
  onSave,
  onClose
}) => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [status, setStatus] = useState<'compliant' | 'non-compliant' | 'partial' | 'not_applicable'>(
    existingFinding?.status || 'compliant'
  );
  const [notes, setNotes] = useState(existingFinding?.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const isReadOnly = audit.status === 'completed' || audit.status === 'cancelled';

  useEffect(() => {
    if (existingFinding) {
      setStatus(existingFinding.status);
      setNotes(existingFinding.notes || '');
    }
  }, [existingFinding]);

  const handleSave = async () => {
    if (!audit.id) return;

    setIsSaving(true);
    try {
      const findings = audit.findings || [];
      let updatedFindings: AuditFinding[];

      if (existingFinding) {
        // Update existing finding
        updatedFindings = findings.map(f => 
          f.id === existingFinding.id
            ? { ...f, status, notes, updatedAt: new Date() }
            : f
        );
      } else {
        // Create new finding
        const newFinding: AuditFinding = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          auditId: audit.id,
          complianceTemplateId,
          complianceTemplateName: complianceTemplate?.name || 'Unknown',
          status,
          notes,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        updatedFindings = [...findings, newFinding];
      }

      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.updateAudit(audit.id, {
        findings: updatedFindings
      }, userInfo);

      if (result.success) {
        showNotification(existingFinding ? 'Finding updated' : 'Finding created', 'success');
        onSave();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error saving finding:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {existingFinding ? 'Edit Finding' : 'Add Finding'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compliance Template
            </label>
            <p className="text-sm text-gray-900">{complianceTemplate?.name || 'Unknown'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status *
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              disabled={isReadOnly}
              className={`w-full px-4 py-2 border border-gray-300 rounded-md ${
                isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            >
              <option value="compliant">Compliant</option>
              <option value="non-compliant">Non-Compliant</option>
              <option value="partial">Partial</option>
              <option value="not_applicable">Not Applicable</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isReadOnly}
              className={`w-full px-4 py-2 border border-gray-300 rounded-md ${
                isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              rows={5}
              placeholder="Document your findings, observations, and any issues..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
          >
            {isReadOnly ? 'Close' : 'Cancel'}
          </button>
          {!isReadOnly && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400"
            >
              {isSaving ? 'Saving...' : 'Save Finding'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditExecutionView;

